import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Container, Row, Col, Card, Badge, Button, Spinner, Alert, Table } from "react-bootstrap";
import { fetchProgramById, fetchProgramSchedules } from "../services/ProgramService";
import { fetchTicketOptions } from "../services/TicketService";
import { getStateNameByCode } from "../services/LocationService";
import ProgramData from "../models/ProgramData";
import TicketOptionData from "../models/TicketOptionData";
import GroupData from "../models/GroupData";
import BookingModal from "../components/BookingModal";
import { isMobileNumber, formatPhoneNumber, getWhatsAppUrl, getPhoneCallUrl } from "../utils/phoneUtils";

interface ScheduleData {
  _id: string;
  programId: string;
  startDate: string;
  endDate: string;
  timing: string;
  address: string;
  pinCode: string;
  state: string;
  country: string;
  organizerName?: string;
  organizerPhone?: string;
}

interface ScheduleWithTickets extends ScheduleData {
  ticketOptions: TicketOptionData[];
}

const ProgramSchedulePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [program, setProgram] = useState<ProgramData | null>(null);
  const [schedules, setSchedules] = useState<ScheduleWithTickets[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stateNames, setStateNames] = useState<{ [key: string]: string }>({});
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const [programData, schedulesData] = await Promise.all([
          fetchProgramById(id),
          fetchProgramSchedules(id)
        ]);
        
        setProgram(programData);
        const schedulesArray = Array.isArray(schedulesData) ? schedulesData : [];
        
        // Fetch ticket options for each schedule
        const schedulesWithTickets = await Promise.all(
          schedulesArray.map(async (schedule: ScheduleData) => {
            try {
              const ticketOptions = await fetchTicketOptions(schedule._id);
              return {
                ...schedule,
                ticketOptions: Array.isArray(ticketOptions) ? ticketOptions : []
              };
            } catch (error) {
              return {
                ...schedule,
                ticketOptions: []
              };
            }
          })
        );
        
        setSchedules(schedulesWithTickets);
        
        // Load state names for all unique states
        const uniqueStates = [...new Set(schedulesArray.map((s: ScheduleData) => s.state))];
        const stateNamePromises = uniqueStates.map(async (stateCode: string) => {
          const name = await getStateNameByCode(stateCode);
          return { code: stateCode, name };
        });
        
        const stateResults = await Promise.all(stateNamePromises);
        const stateMap: { [key: string]: string } = {};
        stateResults.forEach(({ code, name }) => {
          if(!code) return;
          stateMap[code] = name;
        });
        setStateNames(stateMap);
        
      } catch (err) {
        setError("Failed to load program schedules");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isUpcoming = (dateString: string) => {
    return new Date(dateString) > new Date();
  };

  const groupSchedulesByMonth = (schedules: ScheduleWithTickets[]) => {
    // Sort schedules from latest to oldest
    const sortedSchedules = [...schedules].sort((a, b) => 
      new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
    );
    
    const grouped: { [key: string]: ScheduleWithTickets[] } = {};
    sortedSchedules.forEach(schedule => {
      const monthYear = new Date(schedule.startDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long'
      });
      if (!grouped[monthYear]) {
        grouped[monthYear] = [];
      }
      grouped[monthYear].push(schedule);
    });
    return grouped;
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="warning" />
        <p className="mt-3">Loading schedules...</p>
      </Container>
    );
  }

  if (error || !program) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <h4>Schedules Not Found</h4>
          <p>{error || "The requested program schedules could not be found."}</p>
          <Link to="/groups" className="btn btn-warning">Back to Groups</Link>
        </Alert>
      </Container>
    );
  }

  const groupedSchedules = groupSchedulesByMonth(schedules);

  return (
    <Container className="py-4">
      {/* Breadcrumb */}
      <Row className="mb-3">
        <Col xs={12}>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/groups" className="text-decoration-none">Groups</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to={`/play/${program._id}`} className="text-decoration-none">{program.title}</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Schedules
              </li>
            </ol>
          </nav>
        </Col>
      </Row>

      {/* Header */}
      <Row className="mb-4">
        <Col xs={12}>
          <h1 className="display-6 fw-bold mb-2">{program.title} - Show Schedules</h1>
          <p className="lead text-muted">
            Find the perfect show time and location for this amazing production
          </p>
        </Col>
      </Row>

      {schedules.length === 0 ? (
        <Row className="py-5">
          <Col xs={12} className="text-center">
            <i className="bi bi-calendar-x display-1 text-muted mb-3"></i>
            <h4 className="text-muted mb-3">No Schedules Available</h4>
            <p className="text-muted mb-4">
              This production doesn't have any scheduled shows at the moment.
            </p>
            <Link to={`/play/${program._id}`} className="btn btn-warning">
              Back to Play Details
            </Link>
          </Col>
        </Row>
      ) : (
        <>
          {/* Schedule Summary */}
          <Row className="mb-4">
            <Col md={4}>
              <Card className="text-center border-0 bg-light">
                <Card.Body>
                  <h3 className="text-warning mb-1">{schedules.length}</h3>
                  <small className="text-muted">Total Shows</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="text-center border-0 bg-light">
                <Card.Body>
                  <h3 className="text-success mb-1">
                    {schedules.filter(s => isUpcoming(s.startDate)).length}
                  </h3>
                  <small className="text-muted">Upcoming Shows</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="text-center border-0 bg-light">
                <Card.Body>
                  <h3 className="text-primary mb-1">
                    {[...new Set(schedules.map(s => s.state))].length}
                  </h3>
                  <small className="text-muted">States/Locations</small>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Schedules by Month */}
          {Object.entries(groupedSchedules).map(([monthYear, monthSchedules]) => (
            <Row key={monthYear} className="mb-5">
              <Col xs={12}>
                <h3 className="fw-bold mb-4 text-warning">
                  <i className="bi bi-calendar-month me-2"></i>
                  {monthYear}
                </h3>
                <Row className="g-3">
                  {monthSchedules.map((schedule) => (
                    <Col key={schedule._id} lg={6} md={6}>
                      <Card className={`h-100 shadow-sm border-0 ${isUpcoming(schedule.startDate) ? 'border-start border-warning border-3' : ''}`}>
                        <Card.Body>
                          <div className="d-flex justify-content-between align-items-start mb-3">
                            <div>
                              <h5 className="fw-bold mb-1">
                                {formatDate(schedule.startDate)}
                                {schedule.endDate && schedule.endDate !== schedule.startDate && (
                                  <span> - {formatDate(schedule.endDate)}</span>
                                )}
                              </h5>
                              <p className="text-muted mb-0">
                                <i className="bi bi-clock me-1"></i>
                                {schedule.timing}
                              </p>
                            </div>
                            <Badge 
                              bg={isUpcoming(schedule.startDate) ? 'success' : 'secondary'}
                              className="px-3 py-2"
                            >
                              {isUpcoming(schedule.startDate) ? 'Upcoming' : 'Past'}
                            </Badge>
                          </div>
                          
                          <div className="mb-3">
                            <h6 className="fw-semibold mb-2">
                              <i className="bi bi-geo-alt text-warning me-2"></i>
                              Venue
                            </h6>
                            <p className="mb-1">{schedule.address}</p>
                            <small className="text-muted">
                              {schedule.pinCode}, {stateNames[schedule.state] || schedule.state}, {schedule.country}
                            </small>
                          </div>
                          
                          <div className="d-flex justify-content-between align-items-center">
                            <small className="text-muted">
                              {formatTime(schedule.startDate)} - {formatTime(schedule.endDate)}
                            </small>
                            {isUpcoming(schedule.startDate) && (
                              <div className="d-flex gap-2">
                                {schedule.ticketOptions && schedule.ticketOptions.length > 0 ? (
                                  <Button
                                    size="sm"
                                    onClick={() => {
                                      setSelectedSchedule({ ...schedule, programId: program });
                                      setShowBookingModal(true);
                                    }}
                                    style={{ backgroundColor: '#FFD600', color: '#810081', border: 'none' }}
                                  >
                                    <i className="bi bi-ticket-perforated me-1"></i>
                                    Book Tickets
                                  </Button>
                                ) : (
                                  <>
                                    {schedule.organizerPhone && (
                                      <>
                                        {isMobileNumber(schedule.organizerPhone) && (
                                          <Button
                                            size="sm"
                                            variant="outline-primary"
                                            href={getPhoneCallUrl(schedule.organizerPhone)}
                                            title={`Call ${formatPhoneNumber(schedule.organizerPhone)}`}
                                          >
                                            <i className="bi bi-telephone me-1"></i>
                                            Call
                                          </Button>
                                        )}
                                        <Button
                                          size="sm"
                                          variant="outline-success"
                                          href={getWhatsAppUrl(schedule.organizerPhone, `Hi, I'm interested in booking tickets for ${program?.title} on ${formatDate(schedule.startDate)}.`)}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          title={`WhatsApp ${formatPhoneNumber(schedule.organizerPhone)}`}
                                        >
                                          <i className="bi bi-whatsapp me-1"></i>
                                          WhatsApp
                                        </Button>
                                      </>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </Col>
            </Row>
          ))}

          {/* Action Buttons */}
          <Row className="mt-5">
            <Col xs={12} className="text-center">
              <div className="d-flex justify-content-center gap-3">
                <Link to={`/play/${program._id}`} className="btn btn-outline-warning">
                  <i className="bi bi-arrow-left me-2"></i>Back to Play Details
                </Link>
                <Button 
                  variant="warning" 
                  onClick={() => {
                    const upcomingShows = schedules.filter(s => isUpcoming(s.startDate));
                    if (upcomingShows.length > 0) {
                      alert(`There are ${upcomingShows.length} upcoming shows available!`);
                    } else {
                      alert('No upcoming shows available at the moment.');
                    }
                  }}
                >
                  <i className="bi bi-calendar-check me-2"></i>Check Availability
                </Button>
              </div>
            </Col>
          </Row>
        </>
      )}
      
      {/* Booking Modal */}
      <BookingModal
        show={showBookingModal}
        onHide={() => {
          setShowBookingModal(false);
          setSelectedSchedule(null);
        }}
        schedule={selectedSchedule}
      />
    </Container>
  );
};

export default ProgramSchedulePage;